import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Youtube, Twitch, Swords, Trophy, Trash2, Save, ExternalLink, Bookmark, Crown, BarChart3 } from "lucide-react";

const kindLabel = { video: "Video Idea", challenge: "Challenge", build: "Build" };

export default function Dashboard() {
  const { user, setUser } = useAuth();
  const [chars, setChars] = useState({ killers: [], survivors: [] });
  const [form, setForm] = useState({ bio: "", youtube: "", twitch: "", favorite_killer: "", favorite_survivor: "" });
  const [saved, setSaved] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) setForm({
      bio: user.bio || "", youtube: user.youtube || "", twitch: user.twitch || "",
      favorite_killer: user.favorite_killer || "", favorite_survivor: user.favorite_survivor || "",
    });
  }, [user]);

  useEffect(() => {
    // Fetch reference data + saved items once on mount.
    api.get("/dbd/data").then(({ data }) => setChars({ killers: data.killers, survivors: data.survivors })).catch(() => {});
    api.get("/saved").then(({ data }) => setSaved(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      setUser(data);
      toast.success("Profile updated.");
    } catch { toast.error("Could not save profile."); }
    finally { setSaving(false); }
  };

  const removeSaved = async (id) => {
    try { await api.delete(`/saved/${id}`); setSaved((s) => s.filter((i) => i.id !== id)); toast.success("Removed."); }
    catch { toast.error("Could not remove."); }
  };

  const copyShare = async (id) => {
    await navigator.clipboard.writeText(`${window.location.origin}/share/${id}`);
    toast.success("Share link copied!");
  };

  if (!user) return null;

  return (
    <div data-testid="dashboard-page" className="mx-auto max-w-5xl px-6 py-14 md:px-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-5xl tracking-wider md:text-6xl">{user.username}</h1>
          <p className="mt-1 text-xs font-bold uppercase tracking-[0.25em] text-primary">{user.rank}</p>
        </div>
        <Link to={`/u/${user.username}`} className="text-xs font-bold uppercase tracking-widest text-foreground/60 hover:text-primary">View public profile →</Link>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-4">
        <Stat icon={Trophy} label="Score" value={user.score} />
        <Stat icon={Swords} label="Challenges" value={user.challenges_completed} />
        <Stat icon={Bookmark} label="Saved" value={saved.length} />
      </div>

      {user.is_pro ? (
        <div data-testid="pro-membership-card" className="mt-6 flex flex-wrap items-center justify-between gap-4 border border-primary/40 bg-primary/5 p-5">
          <div className="flex items-center gap-3">
            <Crown className="h-6 w-6 text-primary" />
            <div>
              <p className="font-display text-2xl tracking-wide text-primary">Creator Pro</p>
              <p className="text-xs text-foreground/50">{user.plan === "yearly" ? "Yearly plan" : "Monthly plan"}{user.pro_until ? ` · renews ${new Date(user.pro_until).toLocaleDateString()}` : ""}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/pricing" data-testid="dashboard-manage-sub" className="text-xs font-bold uppercase tracking-widest text-foreground/50 underline-offset-4 hover:text-primary hover:underline">
              Manage
            </Link>
            <Link to="/analytics" data-testid="dashboard-analytics-link" className="inline-flex items-center gap-2 border border-primary bg-primary px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-primary-foreground hover:bg-primary/90">
              <BarChart3 className="h-4 w-4" /> View Analytics
            </Link>
          </div>
        </div>
      ) : (
        <div data-testid="upgrade-cta-card" className="mt-6 flex flex-wrap items-center justify-between gap-4 border border-primary/30 bg-gradient-to-r from-primary/10 to-transparent p-5">
          <div className="flex items-center gap-3">
            <Crown className="h-6 w-6 text-primary" />
            <div>
              <p className="font-display text-2xl tracking-wide">Go Creator Pro</p>
              <p className="text-xs text-foreground/50">Unlimited generations, analytics, custom challenges & survivor portraits.</p>
            </div>
          </div>
          <Link to="/pricing" className="inline-flex items-center gap-2 border border-primary bg-primary px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-primary-foreground hover:bg-primary/90">
            <Crown className="h-4 w-4" /> Upgrade
          </Link>
        </div>
      )}

      <div className="mt-10 grid gap-8 lg:grid-cols-5">
        {/* EDIT PROFILE */}
        <div className="lg:col-span-2">
          <h2 className="font-display text-3xl tracking-wide">Edit Profile</h2>
          <div className="mt-4 space-y-4 border border-border bg-card p-6">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-foreground/70">Bio</Label>
              <Textarea data-testid="profile-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="rounded-sm border-white/10 bg-background" placeholder="Tell the fog about yourself…" />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-2 text-xs uppercase tracking-widest text-foreground/70"><Youtube className="h-3.5 w-3.5" /> YouTube</Label>
              <Input data-testid="profile-youtube" value={form.youtube} onChange={(e) => setForm({ ...form, youtube: e.target.value })} className="rounded-sm border-white/10 bg-background" placeholder="https://youtube.com/@you" />
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-2 text-xs uppercase tracking-widest text-foreground/70"><Twitch className="h-3.5 w-3.5" /> Twitch</Label>
              <Input data-testid="profile-twitch" value={form.twitch} onChange={(e) => setForm({ ...form, twitch: e.target.value })} className="rounded-sm border-white/10 bg-background" placeholder="https://twitch.tv/you" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-foreground/70">Favorite Killer</Label>
              <Select value={form.favorite_killer} onValueChange={(v) => setForm({ ...form, favorite_killer: v })}>
                <SelectTrigger data-testid="profile-fav-killer" className="rounded-sm border-white/10 bg-background"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent className="max-h-72 border-white/10 bg-card">{chars.killers.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-foreground/70">Favorite Survivor</Label>
              <Select value={form.favorite_survivor} onValueChange={(v) => setForm({ ...form, favorite_survivor: v })}>
                <SelectTrigger data-testid="profile-fav-survivor" className="rounded-sm border-white/10 bg-background"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent className="max-h-72 border-white/10 bg-card">{chars.survivors.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button data-testid="save-profile-btn" onClick={save} disabled={saving} className="w-full rounded-sm bg-primary font-bold uppercase tracking-widest">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Save Profile</>}
            </Button>
          </div>
        </div>

        {/* SAVED ITEMS */}
        <div className="lg:col-span-3">
          <h2 className="font-display text-3xl tracking-wide">Saved Ideas</h2>
          <div className="mt-4 space-y-3">
            {saved.length === 0 ? (
              <div className="border border-dashed border-white/10 py-16 text-center text-foreground/50">Nothing saved yet. Generate some ideas!</div>
            ) : saved.map((item) => (
              <div key={item.id} data-testid="saved-item" className="border border-border bg-card p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span className="border border-primary/40 bg-primary/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">{kindLabel[item.kind]}</span>
                    <h3 className="mt-2 font-display text-xl tracking-wide">{item.data.title || item.data.build_name || "Untitled"}</h3>
                    <p className="mt-1 line-clamp-2 text-sm text-foreground/60">{item.data.description || item.data.strategy || item.data.hook || ""}</p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <button data-testid={`share-saved-${item.id}`} onClick={() => copyShare(item.id)} className="p-2 text-foreground/50 hover:text-primary" title="Copy share link"><ExternalLink className="h-4 w-4" /></button>
                    <button data-testid={`delete-saved-${item.id}`} onClick={() => removeSaved(item.id)} className="p-2 text-foreground/50 hover:text-primary" title="Delete"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (
  <div className="border border-border bg-card p-5 text-center">
    <Icon className="mx-auto h-5 w-5 text-primary" />
    <p className="mt-2 font-display text-3xl text-foreground">{value}</p>
    <p className="text-[10px] font-bold uppercase tracking-widest text-foreground/50">{label}</p>
  </div>
);
